import React, { useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { findAncestor } from 'utils/dom_utils';
import { handleKeyEvents } from 'utils/handlers';
import slug from 'utils/slug';

import Button from 'components/shared/Button';
import Menu from 'components/shared/Menu';
import Spinner from 'components/shared/Spinner';

type NavigationTileItem = {
  id: string | number;
  sort_index?: number;
  name?: string | null;
};

type NavigationTileDisplay = {
  check: (item: NavigationTileItem) => boolean;
  component: JSX.Element;
};

export type NavigationTileMenuItem = {
  a11yLabel?: string;
  label: string | JSX.Element;
  disabled?: boolean;
  icon: JSX.Element;
  click: (e: React.UIEvent) => void;
};

type NavigationTileProps = {
  item: NavigationTileItem;
  menuAppearance?: 'dark' | 'light';
  name: string;
  image?: string;
  imagePending?: boolean;
  details?: string;
  secondaryDetails?: string;
  menuItems?: NavigationTileMenuItem[];
  displays?: NavigationTileDisplay[];
  className?: string;
  draggable?: boolean;
  disabled?: boolean;
  clickHandler: React.MouseEventHandler;
};

type NavigationDirection = 'up' | 'down' | 'prev' | 'next';

type TileOffset = {
  offsetTop: number;
  offsetLeft: number;
  offsetHeight: number;
  offsetWidth: number;
};

function NavigationTile({
  item,
  name,
  image,
  details,
  secondaryDetails,
  imagePending,
  displays,
  disabled,
  clickHandler,
  className = '',
  menuAppearance = 'light',
  menuItems = [],
  draggable = false,
}: NavigationTileProps) {
  const elem = useRef<HTMLDivElement>(null);

  const display = displays && displays.length > 0 ? displays.find((d) => d.check(item)) : undefined;

  const handleTileSelect = (current: HTMLElement, direction: NavigationDirection = 'next') => {
    if (!elem.current) return;

    const container = findAncestor(elem.current, '.modal-list-items');
    if (!container) return;

    const tiles = Array.from(
      container.querySelectorAll<HTMLButtonElement>('.navigation-tile .image-button')
    );
    const currentTile = tiles.findIndex((t) => current.contains(t));

    function tileOffset(tile: HTMLElement): TileOffset | null {
      if (!tile.parentElement) return null;

      if (tile.parentElement !== container) {
        return tileOffset(tile.parentElement);
      }

      return {
        offsetTop: tile.offsetTop,
        offsetLeft: tile.offsetLeft,
        offsetWidth: tile.offsetWidth,
        offsetHeight: tile.offsetHeight,
      };
    }

    function aboveTile() {
      const curOffset = tileOffset(current);
      if (!curOffset) return null;

      let above = -1;
      let upToLeft = -1;
      let upToRight = -1;

      const rtiles = tiles.reverse();
      above = rtiles.findIndex((t) => {
        const tOffset = tileOffset(t);
        return (
          t !== current &&
          tOffset &&
          tOffset.offsetTop < curOffset.offsetTop &&
          tOffset.offsetLeft === curOffset.offsetLeft
        );
      });

      if (above === -1) {
        const upToLeft = rtiles.findIndex((t) => {
          const tOffset = tileOffset(t);
          return (
            t !== current &&
            tOffset &&
            tOffset.offsetTop < curOffset.offsetTop &&
            tOffset.offsetLeft < curOffset.offsetLeft
          );
        });
      }

      if (upToLeft === -1) {
        upToRight = rtiles.findIndex((t) => {
          const tOffset = tileOffset(t);

          return (
            t !== current &&
            tOffset &&
            tOffset.offsetTop < curOffset.offsetTop &&
            tOffset.offsetLeft < curOffset.offsetLeft + current.offsetWidth
          );
        });
      }

      return (
        (above > -1 && above) ||
        (upToLeft > -1 && upToLeft) ||
        (upToRight > -1 && upToRight) ||
        tiles.length - 1 - currentTile
      );
    }

    function belowTile() {
      const curOffset = tileOffset(current);
      if (!curOffset) return null;

      let below = -1;
      let downToRight = -1;
      let downToLeft = -1;

      below = tiles.findIndex((t) => {
        const tOffset = tileOffset(t);
        return (
          t !== current &&
          tOffset &&
          tOffset.offsetTop > curOffset.offsetTop &&
          tOffset.offsetLeft === curOffset.offsetLeft
        );
      });

      if (below === -1) {
        downToRight = tiles.findIndex((t) => {
          const tOffset = tileOffset(t);
          return (
            t !== current &&
            tOffset &&
            tOffset.offsetTop > curOffset.offsetTop &&
            tOffset.offsetLeft > curOffset.offsetLeft
          );
        });
      }

      if (downToRight === -1) {
        downToLeft = tiles.findIndex((t) => {
          const tOffset = tileOffset(t);
          return (
            t !== current &&
            tOffset &&
            tOffset.offsetTop > curOffset.offsetTop &&
            tOffset.offsetLeft + tOffset.offsetWidth > curOffset.offsetLeft
          );
        });
      }

      return (
        (below > -1 && below) ||
        (downToRight > -1 && downToRight) ||
        (downToLeft > -1 && downToLeft) ||
        currentTile
      );
    }

    function nextTile() {
      return currentTile + 1 >= tiles.length ? 0 : currentTile + 1;
    }

    function prevTile() {
      const pTile = currentTile - 1;
      const wrapTile = tiles.length - 1;
      return pTile <= -1 ? wrapTile : pTile;
    }

    function getDirectionTile(dir: NavigationDirection) {
      switch (dir) {
        case 'up':
          return aboveTile();
        case 'down':
          return belowTile();
        case 'prev':
          return prevTile();
        case 'next':
          return nextTile();
        default:
          return nextTile();
      }
    }

    const nextTileIndex = getDirectionTile(direction);
    if (nextTileIndex !== null && nextTileIndex !== -1) {
      const t = tiles[nextTileIndex];
      t.scrollIntoView({ block: 'center', behavior: 'smooth' });
      t.focus();
    }
  };

  const handleTileSelectNext = (current: HTMLElement) => handleTileSelect(current, 'next');
  const handleTileSelectPrevious = (current: HTMLElement) => handleTileSelect(current, 'prev');
  const handleTileSelectAbove = (current: HTMLElement) => handleTileSelect(current, 'up');
  const handleTileSelectBelow = (current: HTMLElement) => handleTileSelect(current, 'down');

  const handleTileNavigation = (e: React.KeyboardEvent) => {
    const { key, currentTarget } = e;
    const tile = findAncestor(currentTarget as HTMLElement, '.navigation-tile');
    if (tile) {
      switch (key) {
        case 'ArrowDown':
          handleTileSelectBelow(tile);
          break;
        case 'ArrowRight':
          handleTileSelectNext(tile);
          break;
        case 'ArrowUp':
          handleTileSelectAbove(tile);
          break;
        case 'ArrowLeft':
          handleTileSelectPrevious(tile);
          break;
        default:
          e.preventDefault();
          break;
      }
    }
  };

  const handleTileMenuSelect = useCallback((current: EventTarget, direction = 'next') => {
    if (!elem.current) return;

    const menuLinks = elem.current.querySelectorAll<HTMLButtonElement>('.tile-menu-link');
    const currentIndex = Array.from(menuLinks).findIndex((l) => l === current);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex > -1 && newIndex < menuLinks.length) {
      menuLinks[newIndex].focus();
    } else if (newIndex === menuLinks.length) {
      // give focus back to the menu
      const btn = elem.current.querySelector<HTMLButtonElement>('.navigation-tile-menu button');
      if (btn) {
        btn.focus();
      }
    }
  }, []);

  const handleTileMenuSelectNext = useCallback(
    (current: EventTarget) => {
      return handleTileMenuSelect(current, 'next');
    },
    [handleTileMenuSelect]
  );

  const handleTileMenuSelectPrevious = useCallback(
    (current: EventTarget) => {
      return handleTileMenuSelect(current, 'prev');
    },
    [handleTileMenuSelect]
  );

  const handleTileMenuNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, currentTarget } = e;
      e.preventDefault();
      switch (key) {
        case 'ArrowUp':
          handleTileMenuSelectPrevious(currentTarget);
          break;
        case 'ArrowDown':
          handleTileMenuSelectNext(currentTarget);
          break;
        default:
          break;
      }
    },
    [handleTileMenuSelectNext, handleTileMenuSelectPrevious]
  );

  const altText = `${item.name || name || ''}`;

  const showTileMenuTrigger = useMemo(() => menuItems.length > 0, [menuItems]);

  const menuItemsButtons = useMemo(() => {
    return menuItems.map((menuitem) => (
      <Button
        type="cornered"
        className="tile-menu-link"
        role="menuitem"
        disabled={menuitem.disabled}
        onClick={(e) => menuitem.click(e)}
        a11yText={menuitem.a11yLabel}
        onKeyDown={handleKeyEvents({
          open: (e) => menuitem.click(e as React.UIEvent),
          navigate: (e) => handleTileMenuNavigation(e),
        })}
      >
        {menuitem.icon}
        {menuitem.label}
      </Button>
    ));
  }, [menuItems, handleTileMenuNavigation]);

  return (
    <div
      ref={elem}
      data-id={item.id}
      data-sort={item.sort_index === undefined ? item.id : item.sort_index}
      className={`navigation-tile ${className} ${disabled ? 'disabled' : ''}`}
      draggable={draggable}
    >
      <div className="thumb-container">
        {image && (
          <button
            className="image-button"
            onClick={clickHandler}
            disabled={disabled || !!display}
            onKeyDown={handleKeyEvents({ navigate: handleTileNavigation })}
            aria-label={`Select ${name}`}
          >
            <img src={image} alt={altText} draggable="false" />
          </button>
        )}
        {imagePending && (
          <div className="image-pending-container">
            <Spinner size="50" />
          </div>
        )}
        {!image && !imagePending && <FontAwesomeIcon icon="image" size="2x" />}
      </div>

      {display && display.component}

      <div className="tile-info">
        {!display && (
          <React.Fragment>
            {details && <span className="tile-details">{details}</span>}
            {secondaryDetails && <span className="tile-details">{secondaryDetails}</span>}
            <Button type="link" className="tile-name" onClick={clickHandler} disabled={disabled}>
              {name}
            </Button>
          </React.Fragment>
        )}

        {showTileMenuTrigger && (
          <Menu
            disabled={disabled}
            menuItems={menuItemsButtons}
            className="navigation-tile-menu"
            iconOnlyTrigger
            triggerId={`${slug(name)}-${item.id}-navigation-tile`}
            triggerIcon="bars"
            triggerText={`Show menu for ${name}`}
            direction="up"
            position="left"
            appearance={menuAppearance}
          />
        )}
      </div>
    </div>
  );
}

export default NavigationTile;
